import { useCallback, useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Loader2, Stethoscope, Briefcase, Users, Megaphone, Mail, Phone, MapPin, Percent, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const TYPE_META = {
  doctor:       { Icon: Stethoscope, label: "Doctor",      color: "text-[#E85D04]" },
  co_counsel:   { Icon: Briefcase,  label: "Co-counsel",   color: "text-[#11322A]" },
  past_client:  { Icon: Users,      label: "Past client",  color: "text-[#166534]" },
  marketing:    { Icon: Megaphone,  label: "Marketing",    color: "text-[#B45309]" },
  other:        { Icon: Users,      label: "Other",        color: "text-[#5C6B68]" },
};

const BLANK = {
  name: "", type: "doctor", firm_or_practice: "", email: "", phone: "",
  city: "", state: "NY", default_commission_pct: "", notes: "",
};

export default function ReferrersPanel({ open, onOpenChange }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [posting, setPosting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/referrers");
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Name is required");
    setPosting(true);
    try {
      const payload = {
        name: form.name.trim(),
        type: form.type,
        firm_or_practice: form.firm_or_practice.trim() || undefined,
        email: form.email.trim() || undefined,
        phone: form.phone.trim() || undefined,
        city: form.city.trim() || undefined,
        state: form.state || undefined,
        default_commission_pct: form.default_commission_pct
          ? parseFloat(form.default_commission_pct)
          : undefined,
        notes: form.notes.trim() || undefined,
      };
      const { data } = await api.post("/admin/referrers", payload);
      setRows((prev) => [data, ...prev.filter((r) => r.id !== data.id)]);
      setForm(BLANK);
      setShowForm(false);
      toast.success(`Added ${payload.name}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setPosting(false);
    }
  };

  const remove = async (r) => {
    if (!confirm(`Deactivate ${r.name}?`)) return;
    try {
      await api.delete(`/admin/referrers/${r.id}`);
      setRows((prev) => prev.filter((x) => x.id !== r.id));
      toast.success("Referrer deactivated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="referrers-panel">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black text-[#11322A]">
            Referral Sources
          </DialogTitle>
          <p className="text-xs text-[#5C6B68]">
            Doctors, co-counsel, past clients, and marketing channels that send you cases. Track commissions + ROI per source.
          </p>
        </DialogHeader>

        <div className="mt-4 flex items-center justify-between">
          <div className="text-xs font-bold uppercase tracking-wider text-[#5C6B68]">
            {rows.length} active {rows.length === 1 ? "source" : "sources"}
          </div>
          <button onClick={() => setShowForm((v) => !v)} className="btn-primary text-sm" data-testid="referrers-add-toggle">
            <Plus className="h-4 w-4" /> Add referral source
          </button>
        </div>

        {showForm && (
          <form onSubmit={submit} className="mt-4 card-soft p-4" data-testid="referrers-form">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="label-cap">Type</label>
                <select
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value })}
                  className="input-field mt-2"
                  data-testid="referrer-type"
                >
                  {Object.entries(TYPE_META).map(([k, v]) => (
                    <option key={k} value={k}>{v.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label-cap">Name *</label>
                <input
                  className="input-field mt-2"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Dr. Reuben Cohen"
                  required
                  data-testid="referrer-name"
                />
              </div>
              <div>
                <label className="label-cap">Practice / Firm</label>
                <input
                  className="input-field mt-2"
                  value={form.firm_or_practice}
                  onChange={(e) => setForm({ ...form, firm_or_practice: e.target.value })}
                  placeholder="Brooklyn Spine & Sports"
                  data-testid="referrer-firm"
                />
              </div>
              <div>
                <label className="label-cap">Email</label>
                <input
                  type="email"
                  className="input-field mt-2"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="referrer-email"
                />
              </div>
              <div>
                <label className="label-cap">Phone</label>
                <input
                  className="input-field mt-2"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  data-testid="referrer-phone"
                />
              </div>
              <div>
                <label className="label-cap">City</label>
                <input
                  className="input-field mt-2"
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  data-testid="referrer-city"
                />
              </div>
              <div>
                <label className="label-cap">State</label>
                <select
                  value={form.state}
                  onChange={(e) => setForm({ ...form, state: e.target.value })}
                  className="input-field mt-2"
                  data-testid="referrer-state"
                >
                  <option value="NY">NY</option>
                  <option value="NJ">NJ</option>
                  <option value="">—</option>
                </select>
              </div>
              <div>
                <label className="label-cap">Default commission %</label>
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  max="100"
                  className="input-field mt-2"
                  value={form.default_commission_pct}
                  onChange={(e) => setForm({ ...form, default_commission_pct: e.target.value })}
                  placeholder="0–33%"
                  data-testid="referrer-commission"
                />
              </div>
            </div>
            <div className="mt-3">
              <label className="label-cap">Notes</label>
              <textarea
                rows={2}
                className="input-field mt-2"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                data-testid="referrer-notes"
              />
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setShowForm(false)} className="rounded-lg border border-[#E0E4E2] bg-white px-4 py-2 text-sm font-bold text-[#0B1317]">
                Cancel
              </button>
              <button type="submit" disabled={posting} className="btn-primary disabled:opacity-50" data-testid="referrer-submit">
                {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                Add referral source
              </button>
            </div>
          </form>
        )}

        {loading ? (
          <div className="mt-6 grid place-items-center p-12">
            <Loader2 className="h-6 w-6 animate-spin text-[#11322A]" />
          </div>
        ) : rows.length === 0 ? (
          <div className="mt-6 card-soft p-12 text-center text-sm text-[#5C6B68]" data-testid="referrers-empty">
            No referrers yet. Add your first one above to start tracking attribution + commissions.
          </div>
        ) : (
          <ul className="mt-6 divide-y divide-[#E0E4E2] rounded-xl border border-[#E0E4E2] bg-white" data-testid="referrers-list">
            {rows.map((r) => {
              const meta = TYPE_META[r.type] || TYPE_META.other;
              const Icon = meta.Icon;
              return (
                <li key={r.id} className="grid gap-3 p-4 sm:grid-cols-12 sm:items-start" data-testid={`referrer-${r.id}`}>
                  <div className="sm:col-span-4">
                    <div className="flex items-center gap-2">
                      <Icon className={`h-4 w-4 ${meta.color}`} />
                      <div className="font-display text-base font-bold text-[#11322A]">{r.name}</div>
                    </div>
                    <div className="mt-0.5 text-xs text-[#5C6B68]">{meta.label}</div>
                    {r.firm_or_practice && <div className="mt-1 text-xs text-[#0B1317]">{r.firm_or_practice}</div>}
                  </div>
                  <div className="sm:col-span-3 text-xs">
                    {r.email && (
                      <a href={`mailto:${r.email}`} className="inline-flex items-center gap-1 hover:text-[#E85D04]">
                        <Mail className="h-3 w-3" /> {r.email}
                      </a>
                    )}
                    {r.phone && (
                      <div className="mt-0.5 inline-flex items-center gap-1 text-[#5C6B68]">
                        <Phone className="h-3 w-3" /> {r.phone}
                      </div>
                    )}
                    {(r.city || r.state) && (
                      <div className="mt-0.5 inline-flex items-center gap-1 text-[#5C6B68]">
                        <MapPin className="h-3 w-3" /> {[r.city, r.state].filter(Boolean).join(", ")}
                      </div>
                    )}
                  </div>
                  <div className="sm:col-span-2 text-xs">
                    <div className="label-cap text-[9px]">Cases referred</div>
                    <div className="mt-1 font-display text-2xl font-black text-[#11322A]" data-testid={`referrer-cases-${r.id}`}>
                      {r.stats?.total_cases ?? 0}
                    </div>
                    <div className="text-[10px] text-[#5C6B68]">
                      {r.stats?.open ?? 0} open · {r.stats?.closed ?? 0} closed
                    </div>
                  </div>
                  <div className="sm:col-span-2 text-xs">
                    <div className="label-cap text-[9px]">Default fee</div>
                    <div className="mt-1 inline-flex items-center gap-0.5 font-display text-lg font-bold text-[#11322A]">
                      {r.default_commission_pct != null ? (
                        <>
                          {r.default_commission_pct}
                          <Percent className="h-3 w-3" />
                        </>
                      ) : (
                        <span className="text-[#5C6B68]">—</span>
                      )}
                    </div>
                  </div>
                  <div className="sm:col-span-1 flex items-center justify-end">
                    <button
                      onClick={() => remove(r)}
                      className="rounded-md border border-[#F5B6B0] bg-white p-1.5 text-[#B91C1C] hover:bg-[#FDE8E6]"
                      data-testid={`referrer-remove-${r.id}`}
                      aria-label="Remove"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
